import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import logo from "@/assets/dosatronic-logo.png";

export default function ResetPassword() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) { setError(t("auth.invalidLink")); return; }
    if (password !== confirm) { setError(t("auth.passwordsNoMatch")); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6">
      <div className="absolute right-6 top-6"><LanguageSwitcher /></div>
      <div className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="mb-8 inline-flex items-center rounded-md px-2.5 py-2" style={{ backgroundColor: "#ffffff" }}>
          <img src={logo} alt="Dosatronic" className="h-9 w-auto" />
        </div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{t("auth.resetTitle")}</h1>

        {done ? (
          <div className="mt-6">
            <div data-testid="reset-success" className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" strokeWidth={1.75} />
              <span>{t("auth.resetSuccess")}</span>
            </div>
            <Button onClick={() => navigate("/login")} data-testid="go-login"
              className="mt-6 w-full bg-blue-600 text-white hover:bg-blue-700">
              {t("auth.backToLogin")}
            </Button>
          </div>
        ) : (
          <>
            <p className="mt-1 text-sm text-slate-500">{t("auth.resetSubtitle")}</p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("auth.newPassword")}</Label>
                <Input type="password" data-testid="reset-password" value={password}
                  onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-1.5" placeholder="••••••••" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("auth.confirmPassword")}</Label>
                <Input type="password" data-testid="reset-confirm" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} required minLength={6} className="mt-1.5" placeholder="••••••••" />
              </div>
              {error && (
                <div data-testid="reset-error" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {error}
                </div>
              )}
              <Button type="submit" disabled={busy} data-testid="reset-submit"
                className="w-full bg-blue-600 text-white hover:bg-blue-700">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("auth.resetTitle")}
              </Button>
            </form>
            <button type="button" onClick={() => navigate("/login")} data-testid="back-to-login"
              className="mt-6 flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700">
              <ArrowLeft className="h-4 w-4" /> {t("auth.backToLogin")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
